import { Badge, Button, Column, Drawer, DropdownButton, Search, Select, Table } from "@appkit4/react-components";
import { ConfirmationModal, LoaderWrapper, toastMessage } from "components/common/helpers"
import { FC, useState, useCallback } from "react"
import { useParams } from "react-router-dom";
import { searchFilter, selectFilter } from "services/common";
import { ClaimType, SystemUser, UserLevel } from "routes/analysis/types/analysis";
import { useCookies } from "react-cookie";
import { useQueryClient } from "@tanstack/react-query";
import { FetchClients } from "routes/administration/queries/client";
import { FetchClientUsers, DeleteClientUser } from "routes/administration/queries/clientUser";
import { ActiveFilters } from "components/layout/filters/filters";
import UserForm from "./UserForm";
import { userLevels } from "const/common";
import { decodeToken } from "services/common";


const UserView: FC = () => {
  const { clientId } = useParams();
  const [deleteVisible, setDeleteVisible] = useState<SystemUser>();
  const [editVisible, setEditVisible] = useState<SystemUser>();
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState<string[]>();
  const [roleFilter, setRoleFilter] = useState<string[]>();

  const [cookie] = useCookies();

  const queryClient = useQueryClient();

  const { data: clientData, isPending: isPendingClients, error: errorClients } = FetchClients(clientId);

  const { data: userData, isPending: isPendingUsers, error: errorUsers } = FetchClientUsers(clientId);

  const { mutate: deleteUser } = DeleteClientUser();

  const checkUserLevel = (level: UserLevel) => {
    const jwtData = decodeToken(cookie["id_token"]);
    return jwtData?.userLevel && UserLevel[jwtData.userLevel as keyof typeof UserLevel] <= level;
  }

  const handleDelete = async (user?: SystemUser) => {
    if (!user) return;
    deleteUser(user, {
      onSuccess: () => {
        setDeleteVisible(undefined);
        toastMessage("User deleted successfully");
        queryClient.invalidateQueries({ queryKey: ["clientUsers", clientId] });
        return true;
      },
      onError: () => {
        toastMessage("Unable to delete user", null, "error");
      }
    });
  }

  const getData = useCallback(() => {
    if (userData)
      return userData
        .map(m => ({ ...m, userLevel: m.userLevel?.toString(), actions: "", clientAccess: m.claims?.filter(f => f.type === ClaimType.CLIENT).map(m => m.clientId) }))
        .filter(f => searchFilter(f, ["emailAddress", "firstName", "lastName"], search))
        .filter(f => selectFilter(f, "userLevel", roleFilter))
        .filter(f => selectFilter(f, "clientAccess", clientFilter));
    else
      return [];
  }, [userData, search, roleFilter, clientFilter]);

  const customizeColumn = (row: SystemUser, field: string) => {
    switch (field) {
      case "actions":
        if (row.userId)
          return (
            <DropdownButton
              compact
              splitButton
              kind="tertiary"
              data={[
                { label: "Edit user", value: 1, icon: "icon-edit-fill" },
                { label: "Delete user", value: 2, icon: "icon-delete-fill", disabled: !checkUserLevel(UserLevel.PwCAdmin) },
              ]}
              onSelect={(value) => {
                switch (value) {
                  case 1:
                    setEditVisible(row);
                    break;
                  case 2:
                    setDeleteVisible(row);
                    break;
                }
              }}
              onClick={() => setEditVisible(row)}
            >Edit</DropdownButton>
          );
        break;
      case "clientAccess":
        return (<>
          {
            row.clientAccess && row.clientAccess?.length > 0
              ? row.clientAccess?.map((id, i) => (
                <Badge
                  key={id}
                  value={clientData?.find(f => f.clientId === id)?.name}
                  type="info-outlined"
                  className="ap-badge-process"
                />
              ))
              : <Badge
                value={"Full access"}
                type="primary"
                className="ap-badge-process"
              />
          }
        </>)
      case "userLevel":
        return userLevels.find(f => f.value === row.userLevel?.toString())?.label;
      case "lastName":
        return (<>{row.lastName}, {row.firstName}</>)
      case "isActive":
        return row.isActive ? <Badge value="Active" type="success" /> : <Badge value="Inactive" type="danger" />
      default:
        return row[field as keyof SystemUser];
    }
  }

  return (
    <LoaderWrapper loading={[isPendingClients, isPendingUsers]} errors={[errorClients, errorUsers]}>
      <h3>Users</h3>
      {clientId
        ? checkUserLevel(UserLevel.PwCAdmin)
          ? <p>You can only add and edit users within this client's scope. If you need to add admins, you will need to use Administration view.</p>
          : ""
        : <p>In this administrator view, you can review and manage existing users, including PwC users and Client users. Use the New user button to create new users.</p>
      }
      <div className="flex items-center gap-2 mt-4">
        <div className="shrink">
          <Button kind='primary' icon="icon-plus-outline" onClick={() => setEditVisible({ userId: "new", emailAddress: "", firstName: "", lastName: "", userLevel: undefined, isActive: false, claims: [] })} disabled={!checkUserLevel(UserLevel.ClientAdmin)}>New user</Button>
        </div>
        <div>
          <Search
            searchType={"secondary"}
            onChange={(value: string) => {
              setSearch(value);
            }}
            searchValue={search}
            className="list-filter"
          />
        </div>
        <div>
          <Select
            data={Array.from(new Set(userData?.map(m => m.userLevel))).map(m => userLevels.find(f => f.value === m?.toString()))}
            placeholder="Filter by role"
            onSelect={(value) => setRoleFilter(value as string[])}
            value={roleFilter}
            multiple={true}
          />
        </div>
        <div>
          <Select
            data={clientData?.map(m => ({ value: m.clientId, label: m.name }))}
            placeholder="Filter by client"
            onSelect={(value) => setClientFilter(value as string[])}
            value={clientFilter}
            multiple={true}
          />
        </div>
      </div>
      <ActiveFilters rows={getData().length} />
      <Table
        originalData={getData()}
        hasTitle
        striped
      >
        <Column field="lastName" sortKey="lastName" renderCell={customizeColumn}>Name</Column>
        <Column field="emailAddress" sortKey="emailAddress" renderCell={customizeColumn}>Email</Column>
        <Column field="userLevel" sortKey="userLevel" renderCell={customizeColumn}>Role</Column>
        <Column field="clientAccess" sortKey="clientAccess" renderCell={customizeColumn}>Client access</Column>
        <Column field="isActive" sortKey="isActive" renderCell={customizeColumn}>Status</Column>
        <Column field="actions" sortKey="actions" renderCell={customizeColumn}>Actions</Column>
      </Table>
      <ConfirmationModal
        visible={deleteVisible !== undefined}
        title="Delete user"
        cancel={() => setDeleteVisible(undefined)}
        confirm={async () => await handleDelete(deleteVisible)}>
        <p>Are you sure you want to delete {deleteVisible?.emailAddress}?</p>
      </ConfirmationModal>
      <Drawer
        initialFocus={false}
        mask={true}
        resizable={true}
        visible={editVisible !== undefined}
        placement="right"
        title={editVisible?.emailAddress ? `Editing ${editVisible?.emailAddress}` : "Add new user"}
        onClose={() => {
          setEditVisible(undefined);
        }}
        style={{ minWidth: 1000 }}
      >
        {editVisible && clientData && <UserForm
          clientData={clientData}
          userData={editVisible || { userId: "new", emailAddress: "", firstName: "", lastName: "", userLevel: undefined, isActive: false, claims: [] }}
          close={() => {
            setEditVisible(undefined);
          }}
        />}
      </Drawer>
    </LoaderWrapper>
  )
}

export default UserView;
